import { useState } from "react";
import { Navigate } from "react-router-dom";
import { MODULE_LABELS } from "@dentacore/shared-types";
import type { ModuleName } from "@dentacore/shared-types";

import { useCan } from "../auth/use-can";
import styles from "./locked-module.module.css";
import { useModules } from "./modules-context";

/** What each add-on does, in a sentence, for the clinic deciding whether to ask (MOD-D2, MOD-D6). */
export const MODULE_BLURB: Record<ModuleName, string> = {
  // Stock is Supply since MS-D2, so the pitch says so.
  SUPPLY:
    "Count your stock, set each procedure's materials, and keep your suppliers, their prices and your orders in one place.",
  ACCOUNTING:
    "See what you billed, collected and spent, and what each person costs, in one screen.",
  CHATBOT:
    "A WhatsApp assistant that books for your patients, an assistant for your staff, and automatic appointment reminders.",
};

/**
 * What a clinic sees where a module it does not have would be (STORY-188b,
 * MOD-D11): one sentence on what it does and an *Add it* button.
 *
 * **The button sends nothing outside the product.** It leaves a note an
 * operator sees on the clinic's page; asking twice is the same ask. Only
 * somebody who may change the clinic's settings gets the button — everyone
 * else is told whom to ask, because the server would refuse them anyway.
 */
export function LockedModule({ module }: { module: ModuleName }): React.JSX.Element {
  const { request, requested } = useModules();
  const can = useCan();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = MODULE_LABELS[module];
  const asked = requested(module);

  return (
    <section className={styles.locked} data-testid={`locked-${module}`}>
      <h1 className={styles.title}>{label} is an add-on</h1>
      <p className={styles.blurb}>{MODULE_BLURB[module]}</p>
      <p className={styles.note}>Your clinic does not have it yet.</p>

      {asked ? (
        <p className={styles.sent} role="status" data-testid="module-asked">
          You have asked for {label}. We will switch it on for you.
        </p>
      ) : can("settings.clinic.manage") ? (
        <button
          type="button"
          className={styles.add}
          disabled={busy}
          data-testid="module-add"
          onClick={() => {
            setBusy(true);
            setError(null);
            request(module)
              .catch((cause: unknown) => {
                setError(cause instanceof Error ? cause.message : "Could not send that");
              })
              .finally(() => {
                setBusy(false);
              });
          }}
        >
          {busy ? "Asking…" : `Add ${label}`}
        </button>
      ) : (
        <p className={styles.note}>Ask your clinic&rsquo;s admin to add it.</p>
      )}

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * Shows its screen if the clinic has the module, and the prompt if it does not.
 * **Presentation only** — the screen's own calls are refused by the server.
 */
export function ModuleGate({
  module,
  children,
}: {
  module: ModuleName;
  children: React.ReactNode;
}): React.JSX.Element | null {
  const { states, locked, readOnly } = useModules();
  // Nothing until the answer arrives: neither the page nor the lock (QF-D9).
  if (states === null) return null;
  if (locked(module)) return <LockedModule module={module} />;
  return (
    <>
      {readOnly(module) && (
        <p className={styles.readOnly} role="status" data-testid={`read-only-${module}`}>
          {MODULE_LABELS[module]} is switched off for your clinic. You can read what is here, but
          nothing can be changed until it is switched back on.
        </p>
      )}
      {children}
    </>
  );
}

/**
 * **An add-on piece with no locked page of its own: hidden outright** (QF-D9).
 * Chatbot has no sidebar item, so a direct address to one of its pages — such
 * as Settings → Reminders — goes to `to` when the clinic does not have it,
 * rather than drawing a form the server will refuse to save.
 */
export function RequireModule({
  module,
  to,
  children,
}: {
  module: ModuleName;
  to: string;
  children: React.ReactNode;
}): React.JSX.Element | null {
  const { states, has } = useModules();
  if (states === null) return null;
  if (!has(module)) return <Navigate to={to} replace />;
  return <>{children}</>;
}
